import { defineMessages, type MessageDescriptor } from "react-intl";

/**
 * Controlled list of waive reasons surfaced by the V2 Waive modal. The
 * chosen label is persisted into `SyncableTransaction.notes` — no
 * `WaiverReason` enum exists on the backend, so the modal is the source
 * of truth for the list and persistence is by stable label.
 */
export const WAIVE_REASON_VALUES = {
  INDIGENT: "INDIGENT",
  CLINICAL_ERROR: "CLINICAL_ERROR",
  GOODWILL: "GOODWILL",
  POLICY_EXEMPT: "POLICY_EXEMPT",
  OTHER: "OTHER",
} as const;

export type WaiveReasonValue =
  (typeof WAIVE_REASON_VALUES)[keyof typeof WAIVE_REASON_VALUES];

export const WAIVE_REASON_MESSAGES: Record<
  WaiveReasonValue,
  MessageDescriptor
> = defineMessages({
  [WAIVE_REASON_VALUES.INDIGENT]: {
    id: "invoicing.waive.reason.indigent",
    defaultMessage: "Indigent",
  },
  [WAIVE_REASON_VALUES.CLINICAL_ERROR]: {
    id: "invoicing.waive.reason.clinicalError",
    defaultMessage: "Clinical error",
  },
  [WAIVE_REASON_VALUES.GOODWILL]: {
    id: "invoicing.waive.reason.goodwill",
    defaultMessage: "Goodwill",
  },
  [WAIVE_REASON_VALUES.POLICY_EXEMPT]: {
    id: "invoicing.waive.reason.policyExempt",
    defaultMessage: "Policy exempt",
  },
  [WAIVE_REASON_VALUES.OTHER]: {
    id: "invoicing.waive.reason.other",
    defaultMessage: "Other",
  },
});

export const WAIVE_REASON_ORDER: WaiveReasonValue[] = [
  WAIVE_REASON_VALUES.INDIGENT,
  WAIVE_REASON_VALUES.CLINICAL_ERROR,
  WAIVE_REASON_VALUES.GOODWILL,
  WAIVE_REASON_VALUES.POLICY_EXEMPT,
  WAIVE_REASON_VALUES.OTHER,
];

/**
 * Builds the canonical `SyncableTransaction.notes` payload for a
 * waive: e.g. `"GOODWILL"` or `"OTHER: <free text>"`.
 */
export const buildWaiveNotes = (
  reason: WaiveReasonValue,
  freeText: string | undefined,
): string => {
  if (reason === WAIVE_REASON_VALUES.OTHER) {
    const trimmed = (freeText ?? "").trim();
    return trimmed ? `OTHER: ${trimmed}` : "OTHER";
  }
  return reason;
};
